import type React from 'react';

import { Text } from '@change/ds-core-ui';
import { CaretDownIcon, CaretUpIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { useI18n } from '@change-corgi/core/react/i18n';

import { TaskCheckbox } from '../../../TaskCheckbox';
import { ActionButton } from '../ActionButton';

type TaskHeaderProps = Readonly<{
	title: string;
	isFirstActive: boolean;
	isChecked: boolean;
	hasCta: boolean;
	isCompleted: boolean;
	isExpanded: boolean;
	primaryActionText: string;
	onToggleExpand: () => void;
	onCheckboxChange?: (checked: boolean) => void;
	onButtonClick?: () => Promise<void>;
}>;

const getExpandAriaLabel = (isExpanded: boolean, translate: (key: string) => string): string => {
	return isExpanded
		? translate('corgi.petition.starter_dashboard_v2.dynamic_tasks.task.collapse_details_aria')
		: translate('corgi.petition.starter_dashboard_v2.dynamic_tasks.task.expand_details_aria');
};

/**
 * Renders the task header with checkbox, title, action button, and expand/collapse toggle.
 */
export const TaskHeader: React.FC<TaskHeaderProps> = ({
	title,
	isFirstActive,
	isChecked,
	hasCta,
	isCompleted,
	isExpanded,
	primaryActionText,
	onToggleExpand,
	onCheckboxChange,
	onButtonClick,
}) => {
	const { translate } = useI18n();
	const ariaLabel = getExpandAriaLabel(isExpanded, translate);

	return (
		<div className="flex items-center gap-5" data-qa="dynamic-task-header">
			<div className="shrink-0">
				<TaskCheckbox
					name={`dynamic-task-checkbox-${title}`}
					isSelected={isChecked}
					isReadOnly={isChecked}
					onChange={onCheckboxChange}
					ariaLabel={title}
				/>
			</div>
			<div className="flex min-w-0 flex-1 items-center">
				<Text
					variant="body"
					className={cn('text-size-body-bold', isCompleted && 'text-typography-weak line-through')}
					as="p"
					data-qa="dynamic-task-title"
				>
					{title}
				</Text>
			</div>
			<div className="flex shrink-0 items-center gap-5">
				<ActionButton
					isFirstActive={isFirstActive}
					hasCta={hasCta}
					isCompleted={isCompleted}
					primaryActionText={primaryActionText}
					className="hidden w-44 md:inline-flex"
					onClick={onButtonClick}
				/>
				<button
					type="button"
					onClick={onToggleExpand}
					className="bg-background-overlay flex items-center justify-center rounded-full p-1.5"
					aria-label={ariaLabel}
					data-qa="dynamic-task-expand-toggle"
				>
					{isExpanded ? <CaretUpIcon size={24} /> : <CaretDownIcon size={24} />}
				</button>
			</div>
		</div>
	);
};
